import { useRef, useState } from "react";
import { api, formatMoney } from "@/lib/api";
import { parseBrokerData } from "@/lib/brokerImport";

const SHARE_LINK_RE = /https?:\/\/[a-z0-9.-]*tradelocker\.com\/s\/[A-Za-z0-9]+/gi;

export function ImportTradesModal({ open, onClose, accounts, onImport, currency }) {
  const [raw, setRaw] = useState("");
  const [rows, setRows] = useState(null); // parsed trades
  const [checked, setChecked] = useState(new Set());
  const [rowAccounts, setRowAccounts] = useState({}); // idx -> account_id
  const [bulkAccount, setBulkAccount] = useState("");
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setRaw("");
    setRows(null);
    setChecked(new Set());
    setRowAccounts({});
    setBulkAccount("");
    setErrors([]);
  };

  const close = () => { reset(); onClose(); };

  const runParse = async (text) => {
    const links = [...String(text || "").matchAll(SHARE_LINK_RE)].map((m) => m[0]);
    if (links.length) {
      setBusy(true);
      setErrors([]);
      const out = [];
      const errs = [];
      for (const url of links) {
        try {
          const { data } = await api.post("/journal/import/share-link", { url });
          out.push(data);
        } catch (e) {
          errs.push(`${url}: ${e.response?.data?.detail || "failed to fetch"}`);
        }
      }
      setBusy(false);
      setErrors(errs);
      if (out.length) {
        setRows(out);
        setChecked(new Set(out.map((_, i) => i)));
        setRowAccounts({});
      }
      return;
    }
    const { trades, errors: errs } = parseBrokerData(text);
    setErrors(errs);
    if (trades.length) {
      setRows(trades);
      setChecked(new Set(trades.map((_, i) => i)));
      setRowAccounts({});
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setRaw(String(reader.result)); runParse(String(reader.result)); };
    reader.readAsText(f);
    e.target.value = "";
  };

  const toggleRow = (i) => {
    const next = new Set(checked);
    if (next.has(i)) next.delete(i); else next.add(i);
    setChecked(next);
  };

  const allChecked = rows && checked.size === rows.length;
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(rows.map((_, i) => i)));

  const applyBulk = () => {
    if (!bulkAccount) return;
    const next = { ...rowAccounts };
    checked.forEach((i) => { next[i] = bulkAccount === "__none__" ? undefined : bulkAccount; });
    setRowAccounts(next);
  };

  const accountLabel = (id) => accounts.find((a) => a.id === id)?.label || "";

  const logSelected = () => {
    const selected = rows
      .map((t, i) => ({ ...t, account_id: rowAccounts[i] || null, _idx: i }))
      .filter((t) => checked.has(t._idx));
    if (!selected.length) return;
    onImport(selected);
    reset();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-10 px-4" onClick={close}>
      <div
        data-testid="import-modal"
        className="w-full max-w-3xl bg-[#14120F] border border-[#2E2A25] rounded-sm p-6 fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">&mdash; Broker import</p>
            <h2 className="font-head text-2xl text-[#EBE5D9] mt-1">Import trades</h2>
            <p className="text-xs text-[#A39A8E] mt-1">
              Paste rows, TradeLocker share links (tradelocker.com/s/…) or upload a CSV. Columns are auto-detected —
              symbol, side, lots, open/close price and P&amp;L.
            </p>
          </div>
          <button data-testid="import-close" onClick={close} className="font-mono text-xs text-[#A39A8E] hover:text-[#EBE5D9]">✕</button>
        </div>

        {!rows ? (
          <>
            <textarea
              data-testid="import-paste"
              rows={7}
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={"https://demo.tradelocker.com/s/AbC123   ← share link(s)\n— or CSV rows —\nSymbol,Side,Qty,Open Price,Close Price,P&L\nEURUSD,buy,0.50,1.08500,1.09000,250.00"}
              className="mt-4 w-full bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-4 py-3 font-mono text-xs focus:outline-none focus:border-[#CFA66A] rounded-sm resize-none leading-relaxed"
            />
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <button
                data-testid="import-parse-btn"
                onClick={() => runParse(raw)}
                disabled={busy}
                className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-5 py-2 rounded-sm hover:bg-[#EBE5D9] transition-colors disabled:opacity-50"
              >
                {busy ? "Fetching…" : "Parse trades"}
              </button>
              <button
                data-testid="import-file-btn"
                onClick={() => fileRef.current?.click()}
                className="border border-[#2E2A25] text-[#EBE5D9] hover:border-[#CFA66A] px-5 py-2 rounded-sm transition-colors font-mono text-xs uppercase tracking-widest"
              >
                Upload CSV
              </button>
              <input ref={fileRef} data-testid="import-file" type="file" accept=".csv,.txt,.tsv" onChange={onFile} className="hidden" />
              <p className="font-mono text-[10px] text-[#A39A8E]">
                No header? Use: pair, side, lots, entry, exit, pnl
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <select
                data-testid="import-bulk-account"
                value={bulkAccount}
                onChange={(e) => setBulkAccount(e.target.value)}
                className="bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-1.5 rounded-sm focus:outline-none focus:border-[#CFA66A] font-mono text-xs"
              >
                <option value="">Assign selected to…</option>
                <option value="__none__">Unassigned (choose in wizard)</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
              <button
                data-testid="import-bulk-apply"
                onClick={applyBulk}
                className="border border-[#2E2A25] text-[#CFA66A] hover:border-[#CFA66A] font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm transition-colors"
              >
                Apply
              </button>
              <button
                data-testid="import-back"
                onClick={() => { setRows(null); setErrors([]); }}
                className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#A39A8E] hover:text-[#EBE5D9]"
              >
                ← Re-paste
              </button>
            </div>

            <div className="mt-3 border border-[#2E2A25] rounded-sm overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-[#2E2A25] bg-[#1A1815]">
                    <th className="p-2">
                      <input data-testid="import-select-all" type="checkbox" checked={!!allChecked} onChange={toggleAll} className="accent-[#CFA66A]" />
                    </th>
                    {["Pair", "Side", "Lots", "Entry", "Exit", "P&L", "Account"].map((h) => (
                      <th key={h} className="p-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#A39A8E]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t, i) => (
                    <tr key={i} data-testid={`import-row-${i}`} className={`border-b border-[#2E2A25]/50 ${checked.has(i) ? "" : "opacity-40"}`}>
                      <td className="p-2">
                        <input data-testid={`import-row-check-${i}`} type="checkbox" checked={checked.has(i)} onChange={() => toggleRow(i)} className="accent-[#CFA66A]" />
                      </td>
                      <td className="p-2 font-mono text-xs text-[#CFA66A]">{t.pair}</td>
                      <td className="p-2 font-mono text-xs text-[#EBE5D9] uppercase">{t.direction}</td>
                      <td className="p-2 font-mono text-xs text-[#EBE5D9]">{t.lot_size || "—"}</td>
                      <td className="p-2 font-mono text-xs text-[#EBE5D9]">{t.entry || "—"}</td>
                      <td className="p-2 font-mono text-xs text-[#EBE5D9]">{t.exit || "—"}</td>
                      <td className={`p-2 font-mono text-xs ${parseFloat(t.pnl) > 0 ? "text-[#7A9E80]" : parseFloat(t.pnl) < 0 ? "text-[#B56258]" : "text-[#EBE5D9]"}`}>
                        {t.pnl !== "" ? formatMoney(parseFloat(t.pnl), currency) : "—"}
                      </td>
                      <td className="p-2 font-mono text-[10px] text-[#A39A8E]">
                        {rowAccounts[i] ? <span className="text-[#CFA66A]">⌂ {accountLabel(rowAccounts[i])}</span> : "wizard"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
                {checked.size} of {rows.length} selected
              </p>
              <button
                data-testid="import-log-selected"
                onClick={logSelected}
                disabled={!checked.size}
                className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-6 py-2.5 rounded-sm hover:bg-[#EBE5D9] transition-colors disabled:opacity-50"
              >
                Log {checked.size} trade{checked.size !== 1 ? "s" : ""} →
              </button>
            </div>
          </>
        )}

        {errors.length > 0 && (
          <div data-testid="import-errors" className="mt-3 space-y-1">
            {errors.map((e, i) => (
              <p key={i} className="font-mono text-xs text-[#B56258]">{e}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
